import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Checkbox, Input } from "sr/helpers";
import { ReactDatePicker } from "sr/helpers/ui-components/ReactDatePicker";

type Meta = { date: Date | null; description: string };

type Props = {
  open: boolean;
  loading: boolean;
  pendingTrainings: string[];
  pendingCertifications: string[];
  selectedTrainings: string[];
  selectedCertifications: string[];
  trainingMeta: Record<string, Meta>;
  certificationMeta: Record<string, Meta>;
  setSelectedTrainings: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedCertifications: React.Dispatch<React.SetStateAction<string[]>>;
  setTrainingMeta: React.Dispatch<React.SetStateAction<Record<string, Meta>>>;
  setCertificationMeta: React.Dispatch<
    React.SetStateAction<Record<string, Meta>>
  >;
  onClose: () => void;
  onSave: () => void;
};

export const RequiredTrainingCertificationModal = ({
  open,
  loading,
  pendingTrainings,
  pendingCertifications,
  selectedTrainings,
  selectedCertifications,
  trainingMeta,
  certificationMeta,
  setSelectedTrainings,
  setSelectedCertifications,
  setTrainingMeta,
  setCertificationMeta,
  onClose,
  onSave,
}: Props) => {
  const intl = useIntl();
  const [attemptedSave, setAttemptedSave] = React.useState(false);

  React.useEffect(() => {
    if (open) setAttemptedSave(false);
  }, [open]);

  if (!open) return null;

  const hasAnyPending =
    (pendingTrainings?.length || 0) + (pendingCertifications?.length || 0) > 0;
  const allSelected =
    (pendingTrainings || []).every((t) =>
      (selectedTrainings || []).includes(t),
    ) &&
    (pendingCertifications || []).every((c) =>
      (selectedCertifications || []).includes(c),
    );

  const showSelectAll = hasAnyPending && !allSelected;

  const isMetaComplete = (meta?: Meta) =>
    !!meta?.date && !!meta?.description?.trim();

  const incompleteTrainings = (selectedTrainings || []).filter(
    (t) => !isMetaComplete(trainingMeta?.[t]),
  );
  const incompleteCertifications = (selectedCertifications || []).filter(
    (c) => !isMetaComplete(certificationMeta?.[c]),
  );

  const hasIncomplete =
    incompleteTrainings.length > 0 || incompleteCertifications.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 px-3 py-6 sm:px-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="mx-auto w-full max-w-[560px]">
        <div className="rounded-[20px] bg-white shadow-sm shadow-slate-300 overflow-hidden">
          <div className="flex items-start justify-between p-5 sm:p-6 border-b border-neutral-200">
            <h2 className="font-bold text-lg sm:text-xl">
              <FormattedMessage
                id="JOBDETAILS.REQUIRED.TRAINING_CERTIFICATION.MODAL.TITLE"
                defaultMessage="Add Required Trainings and Certifications"
              />
            </h2>
            <button
              type="button"
              className="ml-4 text-2xl leading-none font-semibold text-black/70 hover:text-black"
              onClick={() => {
                if (loading) return;
                onClose();
              }}
              aria-label={intl.formatMessage({
                id: "POPUP.CLOSE.BUTTON",
                defaultMessage: "Close",
              })}
            >
              ×
            </button>
          </div>

          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-gray-700">
                <FormattedMessage
                  id="JOBDETAILS.REQUIRED.TRAINING_CERTIFICATION.MODAL.SUBTITLE"
                  defaultMessage="Select the required items you already have and add details."
                />
              </p>
              {showSelectAll ? (
                <button
                  type="button"
                  className="shrink-0 text-sm font-bold text-[#0078ae]"
                  onClick={() => {
                    setSelectedTrainings(pendingTrainings || []);
                    setSelectedCertifications(pendingCertifications || []);
                    setTrainingMeta((prev) => {
                      const next = { ...(prev || {}) };
                      (pendingTrainings || []).forEach((t) => {
                        if (!next[t]) next[t] = { date: null, description: "" };
                      });
                      return next;
                    });
                    setCertificationMeta((prev) => {
                      const next = { ...(prev || {}) };
                      (pendingCertifications || []).forEach((c) => {
                        if (!next[c]) next[c] = { date: null, description: "" };
                      });
                      return next;
                    });
                  }}
                >
                  <FormattedMessage
                    id="JOBDETAILS.REQUIRED.TRAINING_CERTIFICATION.MODAL.SELECT_ALL"
                    defaultMessage="Select all required"
                  />
                </button>
              ) : null}
            </div>

            <div className="mt-4 space-y-6 max-h-[55vh] overflow-auto pr-1">
              {(pendingTrainings?.length || 0) > 0 && (
                <div className="space-y-3">
                  <div className="font-bold text-sm text-gray-900">
                    <FormattedMessage
                      id="CLEANER.REGISTER.TRAINING_CERTIFICATIONS.LIST.TRAININGS"
                      defaultMessage="Trainings"
                    />
                  </div>
                  <div className="space-y-4">
                    {(pendingTrainings || []).map((title, idx) => {
                      const checked = (selectedTrainings || []).includes(title);
                      const meta = trainingMeta?.[title] || {
                        date: null,
                        description: "",
                      };
                      return (
                        <div
                          key={`missing-training-${title}`}
                          className="w-full"
                        >
                          <Checkbox
                            id={`missing-training-${idx}`}
                            value={title}
                            checked={checked}
                            label={
                              <span className="mt-[1px] text-sm font-medium text-gray-900">
                                {idx + 1}. {title}
                              </span>
                            }
                            labelClass="ml-0"
                            divclass="cursor-pointer"
                            inputClass="accent-[#ff39b3]"
                            onChange={(e: any) => {
                              const nextChecked = Boolean(e?.target?.checked);
                              setSelectedTrainings((prev) => {
                                const list = Array.isArray(prev) ? prev : [];
                                if (nextChecked)
                                  return Array.from(new Set([...list, title]));
                                return list.filter((t) => t !== title);
                              });
                              if (nextChecked) {
                                setTrainingMeta((prev) => ({
                                  ...(prev || {}),
                                  [title]: prev?.[title] || {
                                    date: null,
                                    description: "",
                                  },
                                }));
                              }
                            }}
                          />

                          {checked && (
                            <div className="mt-3 space-y-3 pl-7">
                              <div>
                                <ReactDatePicker
                                  onChange={(date) =>
                                    setTrainingMeta((prev) => ({
                                      ...(prev || {}),
                                      [title]: {
                                        ...(prev?.[title] || {
                                          date: null,
                                          description: "",
                                        }),
                                        date,
                                      },
                                    }))
                                  }
                                  selected={meta.date}
                                  maxDate={new Date()}
                                  isDefaultSelected={!!meta.date}
                                  label={intl.formatMessage({
                                    id: "CLEANER.PROFILE.TRAINING.DATE.ATTENDED",
                                    defaultMessage: "Date Attended",
                                  })}
                                />
                                {attemptedSave && !meta.date && (
                                  <p className="mt-1 text-xs text-red-600">
                                    <FormattedMessage
                                      id="JOBDETAILS.REQUIRED.TRAINING_CERTIFICATION.MODAL.DATE_REQUIRED"
                                      defaultMessage="Date is required"
                                    />
                                  </p>
                                )}
                              </div>
                              <div>
                                <Input
                                  value={meta.description}
                                  onChange={(e) =>
                                    setTrainingMeta((prev) => ({
                                      ...(prev || {}),
                                      [title]: {
                                        ...(prev?.[title] || {
                                          date: null,
                                          description: "",
                                        }),
                                        description: e.target.value,
                                      },
                                    }))
                                  }
                                  label={intl.formatMessage({
                                    id: "CLEANER.PROFILE.TRAINING.INPUT.DESCRIPTION.TITLE",
                                    defaultMessage: "Description",
                                  })}
                                  type="text"
                                  placeholder={intl.formatMessage({
                                    id: "CLEANER.PROFILE.TRAINING.INPUT.DECRIPTION.PLANCEHOLDER",
                                    defaultMessage: "Enter Description",
                                  })}
                                />
                                {attemptedSave && !meta.description?.trim() && (
                                  <p className="mt-1 text-xs text-red-600">
                                    <FormattedMessage
                                      id="JOBDETAILS.REQUIRED.TRAINING_CERTIFICATION.MODAL.DESCRIPTION_REQUIRED"
                                      defaultMessage="Description is required"
                                    />
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {(pendingCertifications?.length || 0) > 0 && (
                <div className="space-y-3">
                  <div className="font-bold text-sm text-gray-900">
                    <FormattedMessage
                      id="CLEANER.REGISTER.TRAINING_CERTIFICATIONS.LIST.CERTIFICATIONS"
                      defaultMessage="Certifications"
                    />
                  </div>
                  <div className="space-y-4">
                    {(pendingCertifications || []).map((title, idx) => {
                      const checked = (selectedCertifications || []).includes(
                        title,
                      );
                      const meta = certificationMeta?.[title] || {
                        date: null,
                        description: "",
                      };
                      return (
                        <div key={`missing-cert-${title}`} className="w-full">
                          <Checkbox
                            id={`missing-cert-${idx}`}
                            value={title}
                            checked={checked}
                            label={
                              <span className="mt-[1px] text-sm font-medium text-gray-900">
                                {idx + 1}. {title}
                              </span>
                            }
                            labelClass="ml-0"
                            divclass="cursor-pointer"
                            inputClass="accent-[#ff39b3]"
                            onChange={(e: any) => {
                              const nextChecked = Boolean(e?.target?.checked);
                              setSelectedCertifications((prev) => {
                                const list = Array.isArray(prev) ? prev : [];
                                if (nextChecked)
                                  return Array.from(new Set([...list, title]));
                                return list.filter((t) => t !== title);
                              });
                              if (nextChecked) {
                                setCertificationMeta((prev) => ({
                                  ...(prev || {}),
                                  [title]: prev?.[title] || {
                                    date: null,
                                    description: "",
                                  },
                                }));
                              }
                            }}
                          />

                          {checked && (
                            <div className="mt-3 space-y-3 pl-7">
                              <div>
                                <ReactDatePicker
                                  onChange={(date) =>
                                    setCertificationMeta((prev) => ({
                                      ...(prev || {}),
                                      [title]: {
                                        ...(prev?.[title] || {
                                          date: null,
                                          description: "",
                                        }),
                                        date,
                                      },
                                    }))
                                  }
                                  selected={meta.date}
                                  maxDate={new Date()}
                                  isDefaultSelected={!!meta.date}
                                  label={intl.formatMessage({
                                    id: "CLEANER.REGISTER.TRAINING_CERTIFICATIONS.DATE.RECEIVED",
                                    defaultMessage: "Date Received",
                                  })}
                                />
                                {attemptedSave && !meta.date && (
                                  <p className="mt-1 text-xs text-red-600">
                                    <FormattedMessage
                                      id="JOBDETAILS.REQUIRED.TRAINING_CERTIFICATION.MODAL.DATE_REQUIRED"
                                      defaultMessage="Date is required"
                                    />
                                  </p>
                                )}
                              </div>
                              <div>
                                <Input
                                  value={meta.description}
                                  onChange={(e) =>
                                    setCertificationMeta((prev) => ({
                                      ...(prev || {}),
                                      [title]: {
                                        ...(prev?.[title] || {
                                          date: null,
                                          description: "",
                                        }),
                                        description: e.target.value,
                                      },
                                    }))
                                  }
                                  label={intl.formatMessage({
                                    id: "CLEANER.PROFILE.TRAINING.INPUT.DESCRIPTION.TITLE",
                                    defaultMessage: "Description",
                                  })}
                                  type="text"
                                  placeholder={intl.formatMessage({
                                    id: "CLEANER.PROFILE.TRAINING.INPUT.DECRIPTION.PLANCEHOLDER",
                                    defaultMessage: "Enter Description",
                                  })}
                                />
                                {attemptedSave && !meta.description?.trim() && (
                                  <p className="mt-1 text-xs text-red-600">
                                    <FormattedMessage
                                      id="JOBDETAILS.REQUIRED.TRAINING_CERTIFICATION.MODAL.DESCRIPTION_REQUIRED"
                                      defaultMessage="Description is required"
                                    />
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-5 sm:p-6 border-t border-neutral-200 flex items-center justify-end gap-3">
            <button
              disabled={loading}
              onClick={() => {
                if (loading) return;
                onClose();
              }}
              className="px-6 py-2.5 rounded-xl border border-[#ff39b3] text-[#ff39b3] hover:bg-red-50 font-bold text-sm disabled:opacity-60"
            >
              <FormattedMessage
                id="RECRUITER.SCHEDULEWORKORDER.EDIT.BUTTON.CANCEL"
                defaultMessage="Cancel"
              />
            </button>
            <button
              disabled={
                loading ||
                (selectedTrainings?.length || 0) +
                  (selectedCertifications?.length || 0) ===
                  0
              }
              onClick={() => {
                setAttemptedSave(true);
                if (hasIncomplete) return;
                onSave();
              }}
              className="px-8 py-2.5 rounded-xl bg-[#ff39b3] text-white hover:bg-navy font-bold text-sm disabled:opacity-60"
            >
              {loading ? (
                <FormattedMessage
                  id="JOBDETAILS.REQUIRED.TRAINING_CERTIFICATION.MODAL.SAVING"
                  defaultMessage="Saving..."
                />
              ) : (
                <FormattedMessage id="JOBS.SAVEBUTTON" defaultMessage="Save" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
